import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import Button from '../../components/buttons/Button';
import CustomLoading from '../../components/customLoading/CustomLoading';
import Table from '../../components/tables/Table';
import { getAllCentersTpt, getTptDosesLastHour, operateTptMachineDoor } from '../../services/doctor/doctor_view_dashboard';
import { ToggleSwitch } from '../../components/ToggleSwitch/ToggleSwitch';
import { BiRefresh } from 'react-icons/bi';
import { IoChevronDown, IoChevronForward } from 'react-icons/io5';

const formatUpdatedAt = (isoString) => {
    if (!isoString) return "—";
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return "—";
    const date = d.toLocaleDateString("en-GB");
    const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    return `${date}, ${time}`;
};

const formatINR = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const normalizeName = (name) => (name || "").trim().toLowerCase();

const TABLE_HEADERS = [
    { label: "Center", key: "center" },
    { label: "Machine", key: "machine" },
    { label: "Updated At", key: "updated" },
    { label: "Usage Today", key: "usage" },
    { label: "Door", key: "door" },
];

const DOSE_GRID_ROWS = [
    { label: "J/CM²", key: "joulepercm" },
    { label: "Tube", key: "tube" },
    { label: "Date", key: "date" },
    { label: "Comment", key: "comment" },
    { label: "Tablets", key: "tablets" },
];

function DoseSheet({ pkg }) {
    const doses = pkg.doses || [];
    const byNo = {};
    doses.forEach((d) => { byNo[d.dose_no] = d; });

    const gridSize = Math.max(pkg.planned_dose_count || 0, doses.length, 18);
    const doseNumbers = Array.from({ length: gridSize }, (_, i) => i + 1);
    const currentNo = pkg.current_dose_no
        ?? doses.find((d) => d.is_current)?.dose_no
        ?? null;

    const cellValue = (num, key) => {
        const d = byNo[num];
        if (!d) return "";
        if (key === "tablets") {
            return d.tablets === undefined || d.tablets === null || d.tablets === "" ? "-" : d.tablets;
        }
        return d[key] || "";
    };

    return (
        <div className="w-full overflow-x-auto">
            <table className="table-auto border-collapse w-full text-[0.6rem]">
                <thead>
                    <tr>
                        <th className="bg-primary text-white border font-bold p-1">Dose</th>
                        {doseNumbers.map((num) => (
                            <th
                                key={num}
                                title={num}
                                className={`border font-bold p-1 ${num === currentNo ? "bg-amber-500 text-white" : "bg-primary text-white"}`}
                            >
                                {num}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {DOSE_GRID_ROWS.map((row) => (
                        <tr key={row.key}>
                            <td className="border font-bold p-1">{row.label}</td>
                            {doseNumbers.map((num) => (
                                <td
                                    key={num}
                                    className={`border p-1 ${num === currentNo ? "bg-amber-50 font-semibold text-amber-900" : ""}`}
                                    style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
                                >
                                    {cellValue(num, row.key)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PackageDoseCard({ pkg }) {
    const [open, setOpen] = useState(false);
    const doses = pkg.doses || [];
    const currentNo = pkg.current_dose_no
        ?? doses.find((d) => d.is_current)?.dose_no
        ?? null;

    return (
        <div className="rounded-lg border border-gray-200 bg-white">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
            >
                <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-800">
                        {pkg.patient?.name || "—"}
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[0.65rem] font-medium text-gray-500">
                            #{pkg.package_id} · {pkg.package_type}
                        </span>
                        {currentNo != null && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[0.65rem] font-semibold text-amber-700">
                                Dose #{currentNo} just entered
                            </span>
                        )}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                        Paid {formatINR(pkg.paid_amount)} of {formatINR(pkg.package_amount)}
                        <span className="text-red-500"> · {formatINR(pkg.remaining_amount)} due</span>
                        {" · "}{doses.length} dose{doses.length === 1 ? "" : "s"}
                    </p>
                </div>
                <span className="shrink-0 text-gray-400">
                    {open ? <IoChevronDown size={18} /> : <IoChevronForward size={18} />}
                </span>
            </button>

            {open && (
                <div className="border-t border-gray-100 px-4 py-3">
                    {/* Amounts */}
                    <div className="mb-3 grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-md bg-gray-50 p-2">
                            <p className="text-[0.6rem] uppercase text-gray-400">Package</p>
                            <p className="text-sm font-bold text-gray-800">{formatINR(pkg.package_amount)}</p>
                        </div>
                        <div className="rounded-md bg-green-50 p-2">
                            <p className="text-[0.6rem] uppercase text-gray-400">Paid</p>
                            <p className="text-sm font-bold text-green-700">{formatINR(pkg.paid_amount)}</p>
                        </div>
                        <div className="rounded-md bg-red-50 p-2">
                            <p className="text-[0.6rem] uppercase text-gray-400">Remaining</p>
                            <p className="text-sm font-bold text-red-600">{formatINR(pkg.remaining_amount)}</p>
                        </div>
                    </div>

                    {/* Dose sheet grid */}
                    <DoseSheet pkg={pkg} />

                    {pkg.last_doctor_comment && (
                        <p className="mt-2 text-xs text-gray-500">
                            <span className="font-semibold text-gray-600">Doctor: </span>
                            {pkg.last_doctor_comment}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

function AllCentersTpt() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [dosesData, setDosesData] = useState(null);
    const [operatingId, setOperatingId] = useState(null);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [tptRes, doseRes] = await Promise.allSettled([
                getAllCentersTpt(),
                getTptDosesLastHour({ hours: 1 }),
            ]);

            if (tptRes.status === "fulfilled" && tptRes.value.status === 200) {
                setData(tptRes.value.data);
            } else {
                toast.error("Unable to fetch TPT machines");
            }

            if (doseRes.status === "fulfilled" && doseRes.value.status === 200) {
                setDosesData(doseRes.value.data);
            } else {
                setDosesData(null);
            }
        } catch (error) {
            toast.error("Unable to fetch TPT machines");
            console.error("Error fetching TPT data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const handleOperate = async (machine) => {
        // Same logic as the clinic sidebar TPT toggle, without the OTP step.
        const nextDoor = machine.is_door === "open" ? "closed" : "open";
        setOperatingId(machine.id);
        try {
            const res = await operateTptMachineDoor(machine.id, nextDoor);
            if (res.status === 200) {
                toast.success(`${machine.machine} door ${nextDoor === "open" ? "opened" : "closed"}`);
                fetchAll();
            } else {
                toast.error("Unable to operate machine");
            }
        } catch (error) {
            toast.error("Unable to operate machine");
            console.error("Error operating machine:", error);
        } finally {
            setOperatingId(null);
        }
    };

    // Centers grouped, named first and the unassigned bucket last.
    const centerGroups = useMemo(() => {
        const centers = data?.centers || [];
        return [...centers]
            .sort((a, b) => {
                if (!a.center_name && b.center_name) return 1;
                if (a.center_name && !b.center_name) return -1;
                return (a.center_name || "").localeCompare(b.center_name || "");
            })
            .map((center) => ({
                name: center.center_name || "Unassigned",
                usageToday: center.tpt_count ?? 0,
                machines: center.tpt_machines || [],
            }));
    }, [data]);

    const totalMachines = useMemo(
        () => centerGroups.reduce((acc, g) => acc + g.machines.length, 0),
        [centerGroups]
    );

    // Recent-dose packages, grouped by center name.
    const doseCenters = useMemo(() => {
        const d = dosesData;
        if (!d) return [];
        let list = [];
        if (Array.isArray(d.centers)) {
            list = d.centers.map((c) => ({
                name: c.center_name || c.center || "Unassigned",
                packages: c.packages || c.tpt_packages || [],
            }));
        } else if (Array.isArray(d.packages)) {
            list = [{ name: "All Centers", packages: d.packages }];
        }
        return list.filter((c) => (c.packages || []).length > 0);
    }, [dosesData]);

    const centersWithDoseActivity = useMemo(() => {
        const set = new Set();
        doseCenters.forEach((c) => set.add(normalizeName(c.name)));
        return set;
    }, [doseCenters]);

    const rows = useMemo(() => {
        const out = [];
        centerGroups.forEach((group) => {
            const usageCell = (
                <span className="font-semibold text-primary">{group.usageToday}</span>
            );
            const hasActivity = centersWithDoseActivity.has(normalizeName(group.name));
            const centerCell = (
                <span className="flex items-center gap-1.5 font-medium text-gray-700">
                    {group.name}
                    {hasActivity && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[0.6rem] font-semibold text-amber-700">
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />
                            Dose requested
                        </span>
                    )}
                </span>
            );
            if (group.machines.length === 0) {
                out.push({
                    center: centerCell,
                    machine: "—",
                    updated: "—",
                    usage: usageCell,
                    door: "—",
                });
                return;
            }
            group.machines.forEach((m, idx) => {
                const isOpen = m.is_door === "open";
                const busy = operatingId === m.id;
                out.push({
                    center: idx === 0 ? centerCell : <span />,
                    machine: <span className="font-medium text-gray-800">{m.machine}</span>,
                    updated: formatUpdatedAt(m.updated_at),
                    usage: idx === 0 ? usageCell : <span />,
                    door: (
                        <div className="flex items-center gap-2 py-2">
                            <span className={`text-xs font-medium ${isOpen ? "text-green-600" : "text-gray-400"}`}>
                                {busy ? "..." : isOpen ? "Open" : "Closed"}
                            </span>
                            <ToggleSwitch
                                label={`tpt-${m.id}`}
                                toggled={isOpen}
                                disabled={busy}
                                onToggle={() => handleOperate(m)}
                            />
                        </div>
                    ),
                });
            });
        });
        return out;
    }, [centerGroups, centersWithDoseActivity, operatingId]);

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Dashboard', link: "/dashboard" },
                { name: 'TPT Machines' },
            ]} />

            <div className="py-2">
                {/* Header */}
                <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <StyledHeadingText text={"TPT Machines — All Centers"} size={"text-base"} />
                            <p className="mt-0.5 text-[0.7rem] text-gray-500">
                                Usage counts are for today only.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {!loading && data && (
                                <span className="rounded-lg bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary">
                                    {data.count ?? 0} TPT usage today
                                </span>
                            )}
                            <Button
                                children={
                                    <span className="flex items-center gap-1">
                                        <BiRefresh size={16} /> Refresh
                                    </span>
                                }
                                onClick={fetchAll}
                                className="bg-primary text-white rounded-lg text-xs"
                            />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-24">
                        <CustomLoading />
                    </div>
                ) : !totalMachines ? (
                    <p className="py-24 text-center text-sm text-gray-500">No TPT machines found.</p>
                ) : (
                    <div className="mt-4">
                        <Table headers={TABLE_HEADERS} rows={rows} />
                    </div>
                )}

                {/* Recent dose requests (last hour) */}
                {!loading && doseCenters.length > 0 && (
                    <div className="mt-6">
                        <StyledHeadingText text={"Recent TPT Dose Requests (last hour)"} size={"text-sm"} />
                        <div className="mt-2 flex flex-col gap-4">
                            {doseCenters.map((center) => (
                                <div
                                    key={center.name}
                                    className="rounded-xl border border-amber-200 bg-amber-50/40 p-4"
                                >
                                    <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
                                        <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
                                        {center.name}
                                        <span className="rounded-full bg-white px-2 py-0.5 text-[0.65rem] font-semibold text-amber-700">
                                            {center.packages.length} package{center.packages.length === 1 ? "" : "s"}
                                        </span>
                                    </p>
                                    <div className="flex flex-col gap-2">
                                        {center.packages.map((pkg) => (
                                            <PackageDoseCard key={pkg.id || pkg.package_id} pkg={pkg} />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export default AllCentersTpt;
